'use client';
import React, { useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import CustomDatePicker from './DatePickerVi';
import { LogGetPaging } from '../services/logService';
import moment from 'moment-timezone';

export interface ExportAllModalProps {
	reportNumber: string;
	reportDate: string;
	memberCount: number;
}

export default function ExportAllModal({
	isOpen,
	onClose,
	onExport,
	isLoading = false,
	courseId = '',
}: {
	isOpen: boolean;
	onClose: () => void;
	onExport: (exportData: ExportAllModalProps) => Promise<void>;
	isLoading?: boolean;
	courseId: string;
}) {
	const [option, setOption] = useState<ExportAllModalProps>({
		reportDate: '',
		reportNumber: '',
		memberCount: 5,
	});
	const [logs, setLogs] = useState<any>([]);
	const [finalLog, setFinalLog] = useState<any>({});

	const fetchLog = async () => {
		const response = await LogGetPaging({
			action: 4,
			endDate: null,
			freeTextSearch: null,
			pageIndex: null,
			pageSize: null,
			startDate: null,
			recordId: courseId,
		});
		setLogs(parseReports(response.result.items));
		setFinalLog(getLatestValidReport(response.result.items));
	};

	function parseReports(data) {
		return data.map((item, index) => {
			const soBaoCaoMatch = item.detail.match(/số báo cáo\s*([^,]+)/i);
			const ngayBCMatch = item.detail.match(/ngày BC:\s*([\d/]+)/i);

			const reportNumber = soBaoCaoMatch ? soBaoCaoMatch[1].trim() : '';
			const reportDate = ngayBCMatch ? ngayBCMatch[1].trim() : '';

			return {
				index,
				creationDate: new Date(item.creationDate),
				reportNumber,
				reportDate,
				detail: item.detail,
				valid: reportNumber !== '' && reportDate !== '',
			};
		}).sort((a, b) => b.creationDate - a.creationDate);
	}

	function getLatestValidReport(data) {
		const parsed = parseReports(data);
		const validReports = parsed.filter((r) => r.valid);

		if (validReports.length === 0) return null;

		// Lọc báo cáo mới nhất theo creationDate
		return validReports.reduce((latest, current) =>
			new Date(current.creationDate) > new Date(latest.creationDate)
				? current
				: latest
		);
	}

	useEffect(() => {
		fetchLog();
	}, [courseId]);
	useEffect(() => {
		if (finalLog !== null) {
			setOption({
				...option,
				reportNumber: finalLog.reportNumber,
				reportDate: moment(finalLog.reportDate, "DD/MM/YYYY").format("YYYY-MM-DD"),
				memberCount: 5,
			});
		}else{
			setOption({
				...option,
				reportNumber: '',
				reportDate: '',
				memberCount: 5,
			});
		}
	}, [finalLog]);

	const handleSubmit = async () => {
		let errors = [];

		if (!option.reportNumber) {
			errors.push('Vui lòng nhập số báo cáo!');
		}

		if (!option.reportDate) {
			errors.push('Vui lòng nhập ngày xuất báo cáo!');
		}

		if (!option.memberCount || option.memberCount < 1) {
			errors.push('Số thành viên mỗi tổ phải lớn hơn 0!');
		}

		if (errors.length > 0) {
			toast.error(errors.join(' \n '));
			return;
		}
		try {
			await onExport(option);
			onClose();
			fetchLog();
		} catch (error) {
			console.error('Export failed:', error);
		}
	};
	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as='div' className='relative z-50' onClose={onClose}>
				<Transition.Child
					as={Fragment}
					enter='ease-out duration-300'
					enterFrom='opacity-0'
					enterTo='opacity-100'
					leave='ease-in duration-200'
					leaveFrom='opacity-100'
					leaveTo='opacity-0'>
					<div className='fixed inset-0 bg-black/50 backdrop-blur-sm' />
				</Transition.Child>

				<div className='fixed inset-0 overflow-y-auto'>
					<div className='flex min-h-full items-center justify-center p-4 text-center'>
						<Transition.Child
							as={Fragment}
							enter='ease-out duration-300'
							enterFrom='opacity-0 scale-95'
							enterTo='opacity-100 scale-100'
							leave='ease-in duration-200'
							leaveFrom='opacity-100 scale-100'
							leaveTo='opacity-0 scale-95'>
							<Dialog.Panel className='w-full max-w-md max-h-[90vh] transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all flex flex-col'>
								<div className='flex justify-between items-center mb-4'>
									<Dialog.Title
										as='h3'
										className='text-lg font-semibold leading-6 text-gray-900'>
										Xuất báo cáo 1
									</Dialog.Title>
									<button
										onClick={onClose}
										className='text-gray-500 hover:text-gray-700 focus:outline-none'>
										<X />
									</button>
								</div>

								<div className='space-y-4 flex-1 overflow-y-auto pr-1'>
									<div>
										<label className='block text-sm font-medium text-gray-700 mb-1'>
											Ngày xuất báo cáo
										</label>
										<CustomDatePicker
											// @ts-ignore
											selected={option.reportDate}
											onChange={(date) =>
												setOption({
													...option,
													reportDate:
														date.toISOString(),
												})
											}
											placeholder='Ngày xuất báo cáo'
											id='exportDate'
											name='exportDate'
											className='h-[38px]'
										/>
									</div>

									<div>
										<label className='block text-sm font-medium text-gray-700 mb-1'>
											Số báo cáo
										</label>
										<input
											type='text'
											min='1'
											value={option.reportNumber}
											onChange={(e) =>
												setOption({
													...option,
													reportNumber:
														e.target.value,
												})
											}
											className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
											placeholder='Nhập số báo cáo'
										/>
									</div>

									<div>
										<label className='block text-sm font-medium text-gray-700 mb-1'>
											Số thành viên mỗi tổ
										</label>
										<input
											type='number'
											min='1'
											value={option.memberCount}
											onChange={(e) =>
												setOption({
													...option,
													memberCount:
														parseInt(e.target.value) || 5,
												})
											}
											className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
											placeholder='Nhập số thành viên mỗi tổ'
										/>
									</div>

									<div>
										<h3 className='mb-2 text-lg font-semibold text-gray-900 dark:text-white'>
											Nhật ký:
										</h3>
										<ul className='w-100 space-y-1 text-gray-500 list-inside dark:text-gray-400'>
											{logs.length > 0
												? logs.map(
														(log: {
															valid: any;
															creationDate: moment.MomentInput;
															detail: string;
														}) => (
															<li className='flex items-center text-sm'>
																<svg
																	className={`w-3.5 h-3.5 me-2 ${
																		log.valid
																			? 'text-green-500'
																			: 'text-gray-500'
																	} dark:text-green-400 shrink-0`}
																	aria-hidden='true'
																	xmlns='http://www.w3.org/2000/svg'
																	fill='currentColor'
																	viewBox='0 0 20 20'>
																	<path d='M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z' />
																</svg>
																{moment(
																	log.creationDate
																).format(
																	'DD/MM/YYYY HH:mm'
																) +
																	': ' +
																	log.detail}
															</li>
														)
												  )
												: ''}
										</ul>
									</div>
								</div>
								<div className='flex justify-end space-x-3 pt-4 mt-4 border-t'>
									<button
										onClick={onClose}
										className='px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500'>
										Hủy
									</button>
									<button
										onClick={handleSubmit}
										disabled={isLoading}
										className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50'>
										{isLoading ? (
											<span className='flex items-center'>
												<svg
													className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
													xmlns='http://www.w3.org/2000/svg'
													fill='none'
													viewBox='0 0 24 24'>
													<circle
														className='opacity-25'
														cx='12'
														cy='12'
														r='10'
														stroke='currentColor'
														strokeWidth='4'></circle>
													<path
														className='opacity-75'
														fill='currentColor'
														d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
												</svg>
												Đang xử lý...
											</span>
										) : (
											'Xuất báo cáo'
										)}
									</button>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
